import type { MetricQueryResponse } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useMetricQuery } from '../_sdk/hooks';
import { aggSchema, decimalsSchema, metricBinding, rangeSchema, unitSchema } from '../_sdk/schema';
import { defineWidget } from '../_sdk/types';

/** Time series with axes, multi-series (PRD VS-042). Recharts, loaded with the widget. */

export const PropsSchema = z.object({
  ...metricBinding,
  range: rangeSchema.default('24h'),
  agg: aggSchema.default('avg'),
  rate: z.boolean().default(false),
  area: z.boolean().default(false),
  unit: unitSchema,
  decimals: decimalsSchema,
});

export type LineChartProps = z.output<typeof PropsSchema>;
export type LineChartData = Pick<MetricQueryResponse, 'series' | 'truncated' | 'step'>;

export default defineWidget<LineChartProps, LineChartData>({
  type: 'line-chart',
  category: 'metric',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  propsSchema: PropsSchema,
  defaults: { range: '24h', agg: 'avg', rate: false, area: false, decimals: 0, tags: {} },
  sourceIds: (p) => [p.sourceId],
  useData: (p) =>
    toDataState(
      useMetricQuery({
        sourceId: p.sourceId,
        name: p.metricName,
        tags: p.tags,
        range: p.range,
        agg: p.agg,
        fn: p.rate ? 'rate' : undefined,
      }),
      {
        select: (r) => ({ series: r.series, truncated: r.truncated, step: r.step }),
        isEmpty: (d) => d.series.every((s) => s.points.length === 0),
        lastUpdated: (d) => {
          const newest = Math.max(0, ...d.series.map((s) => s.points.at(-1)?.[0] ?? 0));
          return newest > 0 ? new Date(newest) : undefined;
        },
      },
    ),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
